import type { GetServerSideProps } from 'next'
import { parse } from 'cookie'
import { KK_LANG_IDS, Locale } from '@model/locales'
import { SESSION_ID_COOKIE } from '@pages/api/auth'
import { ProductSearchFilter, SortOrder } from '@model/product/ProductSearchResult'
import { routerQueryValueToArray } from '@utils/router'
import { getCategoryProductsResult, searchRule } from '@api/categories'
import { checkCatalog } from '@utils/check-catalog'
import { Catalog } from '@model/pricelist/Catalog'
import { getIdFromQueryParam } from '@pages/products'
import { getTagGroupsCached } from '@api/categories-cached'
import { unSlug } from '@utils/slugs'

export { CategoryPage as default } from '@components/pages/CategoryPage'

export const getServerSideProps: GetServerSideProps = async ({ locale, query, ...ctx }) => {
  const cookies = parse(ctx.req.headers.cookie ?? '')

  const { page = '1', slug, brands, minPrice, maxPrice, count = '30', sorting, searchTerm, catalogId } = query
  const id = typeof slug === 'string' ? unSlug(slug) : undefined
  const currentPage = parseInt(page as string)
  const pageLimit = parseInt(count as string)
  const offset = pageLimit * currentPage - pageLimit
  const currentLocale = (locale as Locale) || 'fi'
  const languageId = KK_LANG_IDS[currentLocale]
  const sessionId = cookies[SESSION_ID_COOKIE]
  const searchOptions: Partial<ProductSearchFilter> = searchTerm
    ? { searchText: routerQueryValueToArray(searchTerm)?.[0] || undefined, searchTextRule: searchRule.SEARCH_EXACT }
    : {}
  const catalog = await checkCatalog(locale as Locale, catalogId)

  if (!catalog) {
    return {
      notFound: true,
    }
  }

  const filters: ProductSearchFilter = {
    categoryId: id ? parseInt(id as string) : undefined,
    languageId,
    catalogId: catalog as Catalog,
    manufacturerIds: brands ? brands.toString().split(',').map(getIdFromQueryParam) : undefined,
    priceFrom: minPrice ? parseFloat(minPrice.toString()) : undefined,
    priceTo: maxPrice ? parseFloat(maxPrice.toString()) : undefined,
    limit: pageLimit,
    offset,
    orderBy: sorting as SortOrder,
    ...searchOptions,
  }

  try {
    const tagGroups = await getTagGroupsCached(languageId)
    const categoryData = await getCategoryProductsResult(filters, pageLimit, offset, tagGroups, sessionId)

    if (!categoryData || !categoryData.productArray.length) {
      return {
        notFound: true,
      }
    }
    return {
      props: {
        catalogs: [],
      },
    }
  } catch (error) {
    console.error('Error fetching category data:', error)
    return {
      notFound: true,
    }
  }
}
